<template>
  <span ref="boxEl" class="UIAnimatedText" :data-shimmer="isShimmering">
    <Transition
      name="UIAnimatedText-swap"
      @beforeLeave="holdCurrentWidth"
      @enter="easeToWidth"
      @afterEnter="releaseWidth"
    >
      <span :key="text" class="UIAnimatedText__text">{{ text }}</span>
    </Transition>
  </span>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

export interface Props {
  /** Text to show. A new value swaps in the way the chat's “Thinking” label does. */
  text: string
  /** A darker band sweeps across the text, left to right, to show work in progress. */
  isShimmering?: boolean
}

withDefaults(defineProps<Props>(), {
  isShimmering: false,
})

/**
 * The box's width eases during a swap, so whatever follows the text glides instead of jumping.
 * It is only pinned for the swap itself: at rest it sizes to its text, so a late font load or a
 * resize never leaves the text clipped.
 */
const boxEl = ref<HTMLElement>()

/** The swap starts with the outgoing text still in the flow: pin the box at its width. */
const holdCurrentWidth = () => {
  const box = boxEl.value
  if (!box) return
  box.style.width = `${box.offsetWidth}px`
  // Commit the pinned width, so the change `easeToWidth` makes next animates from it.
  void box.offsetWidth
}

/**
 * Ease towards the incoming text. `scrollWidth`, not `offsetWidth`: the text is clipped to the
 * pinned box until then, and the box should aim for its full width (up to its `max-width`).
 */
const easeToWidth = (el: Element) => {
  const box = boxEl.value
  if (box) box.style.width = `${(el as HTMLElement).scrollWidth}px`
}

const releaseWidth = () => {
  if (boxEl.value) boxEl.value.style.width = ''
}
</script>

<style lang="scss" scoped>
// The same swap as `UIChatTypingIndicator`'s label: the texts cross-fade while rising by a hair.
$swap-shift: 0.25rem;
$swap-easing: cubic-bezier(0.4, 0, 0.2, 1);

.UIAnimatedText {
  // Shimmer colours: the text in the chat's muted label colour, with a darker band passing
  // over it. Set them on the component to suit other surroundings.
  --shimmer-base-color: var(--text-tertiary);
  --shimmer-highlight-color: var(--text-primary);

  position: relative;
  display: inline-block;
  max-width: 100%;
  // Clips the outgoing text horizontally as the box resizes; the vertical padding (pulled back
  // out with a matching negative margin) leaves the rise room so it is not clipped too.
  overflow: hidden;
  padding-block: $swap-shift * 1.5;
  margin-block: -$swap-shift * 1.5;
  vertical-align: top;
  transition: width 0.28s $swap-easing;

  .UIAnimatedText__text {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    // The box is width-constrained while it eases, so the text must not reflow inside it.
    white-space: nowrap;
    vertical-align: top;
  }

  // The gradient is three texts wide with the band in the middle, so a sweep from one end to
  // the other shows the band crossing for half the cycle and rests off-text for the other half.
  &[data-shimmer='true'] .UIAnimatedText__text {
    background: linear-gradient(
        90deg,
        var(--shimmer-base-color) 45%,
        var(--shimmer-highlight-color) 50%,
        var(--shimmer-base-color) 55%
      )
      0 0 / 300% 100%;
    // No autoprefixer in this build, and Safari still wants the prefix for `text`.
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: UIAnimatedText-shimmer 2.4s linear infinite;
  }

  // Both texts occupy the same spot during the swap: the outgoing one leaves the flow so the
  // box tracks the incoming text.
  .UIAnimatedText-swap-leave-active {
    position: absolute;
    inset-block-start: $swap-shift * 1.5;
    inset-inline-start: 0;
    transition:
      opacity 0.18s ease-in,
      transform 0.18s ease-in;
  }

  .UIAnimatedText-swap-enter-active {
    // Held back a touch so the outgoing text has begun to clear before this one arrives.
    transition:
      opacity 0.28s $swap-easing 0.06s,
      transform 0.28s $swap-easing 0.06s;
  }

  .UIAnimatedText-swap-enter-from {
    opacity: 0;
    transform: translateY($swap-shift);
  }

  .UIAnimatedText-swap-leave-to {
    opacity: 0;
    transform: translateY(-$swap-shift);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &[data-shimmer='true'] .UIAnimatedText__text {
      animation: none;
    }

    .UIAnimatedText-swap-enter-active,
    .UIAnimatedText-swap-leave-active {
      transition: opacity 0.15s linear;
    }

    .UIAnimatedText-swap-enter-from,
    .UIAnimatedText-swap-leave-to {
      transform: none;
    }
  }
}

// Right to left in background position, which moves the band left to right across the text.
@keyframes UIAnimatedText-shimmer {
  from {
    background-position: 100% 0;
  }

  to {
    background-position: 0% 0;
  }
}
</style>
